import { supabase } from '../lib/supabase';

export const infrastructureMonitoringService = {
  // ============================================================================
  // API PERFORMANCE METRICS
  // ============================================================================

  async getApiPerformanceMetrics(filters = {}) {
    let query = supabase?.from('api_performance_metrics')?.select('*')?.order('recorded_at', { ascending: false });

    if (filters?.endpointName) {
      query = query?.eq('endpoint_name', filters?.endpointName);
    }

    if (filters?.status) {
      query = query?.eq('endpoint_status', filters?.status);
    }

    if (filters?.timeRange) {
      const timeRanges = {
        '1h': 1,
        '6h': 6,
        '24h': 24,
        '7d': 168
      };
      const hours = timeRanges?.[filters?.timeRange] || 24;
      const startTime = new Date(Date.now() - hours * 60 * 60 * 1000)?.toISOString();
      query = query?.gte('recorded_at', startTime);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(metric => ({
      id: metric?.id,
      endpointName: metric?.endpoint_name,
      endpointPath: metric?.endpoint_path,
      httpMethod: metric?.http_method,
      responseTimeMs: metric?.response_time_ms,
      statusCode: metric?.status_code,
      endpointStatus: metric?.endpoint_status,
      errorMessage: metric?.error_message,
      requestCount: metric?.request_count,
      successCount: metric?.success_count,
      errorCount: metric?.error_count,
      recordedAt: metric?.recorded_at,
      createdAt: metric?.created_at
    }));
  },

  async getApiPerformanceSummary() {
    const { data, error } = await supabase?.from('api_performance_metrics')?.select('*')?.gte('recorded_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString());

    if (error) throw error;

    const metrics = data || [];
    const totalRequests = metrics?.reduce((sum, m) => sum + (m?.request_count || 0), 0);
    const totalSuccess = metrics?.reduce((sum, m) => sum + (m?.success_count || 0), 0);
    const totalErrors = metrics?.reduce((sum, m) => sum + (m?.error_count || 0), 0);
    const avgResponseTime = metrics?.length > 0
      ? metrics?.reduce((sum, m) => sum + (m?.response_time_ms || 0), 0) / metrics?.length
      : 0;

    const healthyCount = metrics?.filter(m => m?.endpoint_status === 'healthy')?.length;
    const degradedCount = metrics?.filter(m => m?.endpoint_status === 'degraded')?.length;
    const criticalCount = metrics?.filter(m => m?.endpoint_status === 'critical')?.length;

    return {
      totalRequests,
      totalSuccess,
      totalErrors,
      successRate: totalRequests > 0 ? ((totalSuccess / totalRequests) * 100)?.toFixed(2) : 0,
      avgResponseTime: avgResponseTime?.toFixed(2),
      healthyEndpoints: healthyCount,
      degradedEndpoints: degradedCount,
      criticalEndpoints: criticalCount,
      totalEndpoints: metrics?.length
    };
  },

  // ============================================================================
  // DATABASE PERFORMANCE METRICS
  // ============================================================================

  async getDatabasePerformanceMetrics(filters = {}) {
    let query = supabase?.from('database_performance_metrics')?.select('*')?.order('recorded_at', { ascending: false });

    if (filters?.queryType) {
      query = query?.eq('query_type', filters?.queryType);
    }

    if (filters?.healthStatus) {
      query = query?.eq('health_status', filters?.healthStatus);
    }

    if (filters?.timeRange) {
      const timeRanges = {
        '1h': 1,
        '6h': 6,
        '24h': 24,
        '7d': 168
      };
      const hours = timeRanges?.[filters?.timeRange] || 24;
      const startTime = new Date(Date.now() - hours * 60 * 60 * 1000)?.toISOString();
      query = query?.gte('recorded_at', startTime);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(metric => ({
      id: metric?.id,
      queryType: metric?.query_type,
      queryName: metric?.query_name,
      executionTimeMs: metric?.execution_time_ms,
      rowsAffected: metric?.rows_affected,
      connectionPoolActive: metric?.connection_pool_active,
      connectionPoolIdle: metric?.connection_pool_idle,
      connectionPoolTotal: metric?.connection_pool_total,
      transactionThroughput: metric?.transaction_throughput,
      healthStatus: metric?.health_status,
      optimizationNotes: metric?.optimization_notes,
      recordedAt: metric?.recorded_at,
      createdAt: metric?.created_at
    }));
  },

  async getDatabasePerformanceSummary() {
    const { data, error } = await supabase?.from('database_performance_metrics')?.select('*')?.gte('recorded_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString());

    if (error) throw error;

    const metrics = data || [];
    const avgExecutionTime = metrics?.length > 0
      ? metrics?.reduce((sum, m) => sum + (m?.execution_time_ms || 0), 0) / metrics?.length
      : 0;
    const avgThroughput = metrics?.length > 0
      ? metrics?.reduce((sum, m) => sum + (m?.transaction_throughput || 0), 0) / metrics?.length
      : 0;

    const latestMetric = metrics?.[0] || {};
    const poolUtilization = latestMetric?.connection_pool_total > 0
      ? ((latestMetric?.connection_pool_active / latestMetric?.connection_pool_total) * 100)?.toFixed(2)
      : 0;

    const optimalCount = metrics?.filter(m => m?.health_status === 'optimal')?.length;
    const warningCount = metrics?.filter(m => m?.health_status === 'warning')?.length;
    const criticalCount = metrics?.filter(m => m?.health_status === 'critical')?.length;

    return {
      avgExecutionTime: avgExecutionTime?.toFixed(2),
      avgThroughput: avgThroughput?.toFixed(2),
      connectionPoolActive: latestMetric?.connection_pool_active || 0,
      connectionPoolIdle: latestMetric?.connection_pool_idle || 0,
      connectionPoolTotal: latestMetric?.connection_pool_total || 0,
      poolUtilization,
      optimalQueries: optimalCount,
      warningQueries: warningCount,
      criticalQueries: criticalCount,
      totalQueries: metrics?.length
    };
  },

  // ============================================================================
  // CLIENT RESOURCE UTILIZATION
  // ============================================================================

  async getClientResourceUtilization(filters = {}) {
    let query = supabase?.from('client_resource_utilization')?.select('*, company:company_settings(company_name)')?.order('recorded_at', { ascending: false });

    if (filters?.companyId) {
      query = query?.eq('company_id', filters?.companyId);
    }

    if (filters?.alertLevel) {
      query = query?.eq('alert_level', filters?.alertLevel);
    }

    if (filters?.timeRange) {
      const timeRanges = {
        '1h': 1,
        '6h': 6,
        '24h': 24,
        '7d': 168
      };
      const hours = timeRanges?.[filters?.timeRange] || 24;
      const startTime = new Date(Date.now() - hours * 60 * 60 * 1000)?.toISOString();
      query = query?.gte('recorded_at', startTime);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(resource => ({
      id: resource?.id,
      companyId: resource?.company_id,
      companyName: resource?.company?.company_name || 'Unknown',
      cpuUsagePercentage: resource?.cpu_usage_percentage,
      memoryUsageMb: resource?.memory_usage_mb,
      memoryUsagePercentage: resource?.memory_usage_percentage,
      storageUsageMb: resource?.storage_usage_mb,
      storageUsagePercentage: resource?.storage_usage_percentage,
      activeConnections: resource?.active_connections,
      apiRequestsCount: resource?.api_requests_count,
      databaseQueriesCount: resource?.database_queries_count,
      alertLevel: resource?.alert_level,
      capacityPlanningNotes: resource?.capacity_planning_notes,
      loadBalancingRecommendation: resource?.load_balancing_recommendation,
      recordedAt: resource?.recorded_at,
      createdAt: resource?.created_at
    }));
  },

  async getClientResourceSummary() {
    const { data, error } = await supabase?.from('client_resource_utilization')?.select('*')?.gte('recorded_at', new Date(Date.now() - 1 * 60 * 60 * 1000)?.toISOString())?.order('recorded_at', { ascending: false })?.limit(1);

    if (error) throw error;

    const latestMetric = data?.[0] || {};

    return {
      cpuUsage: latestMetric?.cpu_usage_percentage || 0,
      memoryUsage: latestMetric?.memory_usage_percentage || 0,
      storageUsage: latestMetric?.storage_usage_percentage || 0,
      activeConnections: latestMetric?.active_connections || 0,
      alertLevel: latestMetric?.alert_level || 'normal'
    };
  },

  // ============================================================================
  // INFRASTRUCTURE ALERTS
  // ============================================================================

  async getInfrastructureAlerts(filters = {}) {
    let query = supabase?.from('infrastructure_alerts')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.alertStatus) {
      query = query?.eq('alert_status', filters?.alertStatus);
    }

    if (filters?.severity) {
      query = query?.eq('severity', filters?.severity);
    }

    if (filters?.affectedComponent) {
      query = query?.eq('affected_component', filters?.affectedComponent);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(alert => ({
      id: alert?.id,
      alertType: alert?.alert_type,
      severity: alert?.severity,
      title: alert?.title,
      message: alert?.message,
      affectedComponent: alert?.affected_component,
      thresholdValue: alert?.threshold_value,
      currentValue: alert?.current_value,
      alertStatus: alert?.alert_status,
      escalationLevel: alert?.escalation_level,
      escalatedAt: alert?.escalated_at,
      resolvedAt: alert?.resolved_at,
      resolutionNotes: alert?.resolution_notes,
      metadata: alert?.metadata,
      createdAt: alert?.created_at,
      updatedAt: alert?.updated_at
    }));
  },

  async updateAlertStatus(alertId, status, resolutionNotes = null) {
    const updateData = {
      alert_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'resolved') {
      updateData.resolved_at = new Date()?.toISOString();
      if (resolutionNotes) {
        updateData.resolution_notes = resolutionNotes;
      }
    }

    const { data, error } = await supabase?.from('infrastructure_alerts')?.update(updateData)?.eq('id', alertId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ============================================================================
  // PERFORMANCE TRENDS
  // ============================================================================

  async getPerformanceTrends(filters = {}) {
    let query = supabase?.from('performance_trends')?.select('*')?.order('recorded_at', { ascending: false });

    if (filters?.metricType) {
      query = query?.eq('metric_type', filters?.metricType);
    }

    if (filters?.timePeriod) {
      query = query?.eq('time_period', filters?.timePeriod);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(trend => ({
      id: trend?.id,
      metricType: trend?.metric_type,
      metricName: trend?.metric_name,
      timePeriod: trend?.time_period,
      averageValue: trend?.average_value,
      minValue: trend?.min_value,
      maxValue: trend?.max_value,
      trendDirection: trend?.trend_direction,
      percentageChange: trend?.percentage_change,
      predictionNextPeriod: trend?.prediction_next_period,
      capacityPlanningAlert: trend?.capacity_planning_alert,
      recordedAt: trend?.recorded_at,
      createdAt: trend?.created_at
    }));
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToAlerts(callback) {
    const channel = supabase?.channel('infrastructure_alerts_channel')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'infrastructure_alerts'
        },
        (payload) => {
          const alert = {
            id: payload?.new?.id,
            alertType: payload?.new?.alert_type,
            severity: payload?.new?.severity,
            title: payload?.new?.title,
            message: payload?.new?.message,
            affectedComponent: payload?.new?.affected_component,
            alertStatus: payload?.new?.alert_status,
            createdAt: payload?.new?.created_at
          };
          callback(alert, payload?.eventType);
        }
      )?.subscribe();

    return channel;
  },

  unsubscribeFromAlerts(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default infrastructureMonitoringService;